import { useState } from 'react';
import PropTypes from 'prop-types';

import { Link } from 'react-router';

import { connect } from 'react-redux';

import noop from 'lodash/fp/noop';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { LazyImg } from 'Components/LazyImg';
import { Typography } from 'Components/Typography';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import newBadge from 'assets/images/productPages/new-product-badge.png';

import { formatPriceWithCurrency } from 'utils/currencies';

import * as trackingActions from 'dux/tracking/actions';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 280px;
  padding-bottom: ${legacyTheme.spacing.s40};

  color: inherit;

  ${legacyTheme.breakpoints.match('sm')} {
    width: 100%;
    max-width: 450px;
    padding: 0;
    padding-bottom: ${legacyTheme.spacing.s40};
  }
`;

const Label = styled(Typography)`
  position: relative;

  // "NEW" prefix place before label (e.g. supplements)
  & > p {
    position: absolute;
    top: -4px;
    left: -33px;

    ${legacyTheme.breakpoints.match('sm')} {
      position: relative;
      top: auto;
      left: auto;

      text-align: center;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 250px;

  margin-top: ${theme.spacing['8x']};
  margin-bottom: ${theme.spacing['6x']};
  ${legacyTheme.breakpoints.match('sm')} {
    width: auto;
    height: auto;
    max-width: 250px;
    margin-top: ${theme.spacing['6x']};
  }
`;

const StyledImage = styled(Image)`
  position: absolute;
  top: 20px;
  left: 20px;
`;

const ProductsItem = ({
  bgLabelledPicture,
  bgPicture,
  bgPictureAlt,
  capacity,
  capacityVariant,
  currency,
  dataClick,
  dataFrom,
  label,
  labelVariant,
  price,
  productPageUrl,
  slug,
  trackHover,
  hasMultiplePrices,
  showCapacity,
  tag,
  showPrice,
  subLabel,
}) => {
  const [isHovered, setHovered] = useState(false);

  return (
    <Card
      data-click={`${dataClick}-${slug}`}
      data-from={dataFrom}
      data-testid={`product-item-card-${slug}`}
      onMouseEnter={() => {
        setHovered(true);
        trackHover({ dataFrom, dataClick, productSlug: slug });
      }}
      onMouseLeave={() => setHovered(false)}
      to={productPageUrl}
    >
      <ImageContainer>
        {tag === 'new' && <StyledImage height={50} src={newBadge} width={50} />}
        <LazyImg
          alt={bgPictureAlt ?? label}
          src={isHovered ? bgLabelledPicture.src : bgPicture.src}
          width="100%"
        />
      </ImageContainer>

      <Label markupName="h2" paragraph variant={labelVariant}>
        {label}
      </Label>

      <Typography color="noir" variant={capacityVariant}>
        {hasMultiplePrices ? `From ` : null}
        {showPrice && formatPriceWithCurrency({ price, currency })}
        {showCapacity && capacity ? `, ${capacity}` : ''}
        {subLabel}
      </Typography>
    </Card>
  );
};

ProductsItem.propTypes = {
  bgLabelledPicture: srcOrNextImageProp.isRequired,
  bgPicture: srcOrNextImageProp.isRequired,
  bgPictureAlt: PropTypes.string,
  capacity: PropTypes.string,
  capacityVariant: PropTypes.string,
  currency: PropTypes.string.isRequired,
  dataClick: PropTypes.string,
  dataFrom: PropTypes.string,
  label: PropTypes.string.isRequired,
  labelVariant: PropTypes.string,
  price: PropTypes.number.isRequired,
  productPageUrl: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  trackHover: PropTypes.func,
  hasMultiplePrices: PropTypes.bool.isRequired,
  showCapacity: PropTypes.bool,
  showPrice: PropTypes.bool,
  tag: PropTypes.string,
  subLabel: PropTypes.string,
};

ProductsItem.defaultProps = {
  capacity: null,
  capacityVariant: 'p2',
  dataClick: 'see-product',
  dataFrom: 'products-block',
  bgPictureAlt: null,
  labelVariant: 'h3',
  trackHover: noop,
  showCapacity: true,
  tag: '',
  subLabel: '',
  showPrice: true,
};

export default connect(() => ({}), {
  trackHover: trackingActions.trackHover,
})(ProductsItem);
